import PropTypes from "prop-types";
import React, { useEffect, useRef, useCallback } from "react";

//Import Icons
import FeatherIcon from "feather-icons-react";

// //Import Scrollbar
import SimpleBar from "simplebar-react";

//i18n
import { withTranslation } from "react-i18next";

// MetisMenu
import MetisMenu from "metismenujs";
import { Link, useLocation } from "react-router-dom";

import withRouter from "../../../src/components/Common/withRouter";
import MenuPermissionGate from "../Common/MenuPermissionGate";
import { useAuth } from "../../contexts/AuthContext";

const SidebarContent = (props: any) => {
  const ref: any = useRef();
  const metisRef = useRef<{ dispose?: () => void } | null>(null);
  const { loading: authLoading, roles, capabilities, isCustomerPortalUser } = useAuth();
  // Re-init MetisMenu after permission-gated items mount/change (avoids "all open" glitch).
  const menuSignature = `${authLoading}:${roles.size}:${capabilities.size}:${isCustomerPortalUser()}`;

  const clearMenuActivation = useCallback(() => {
    const ul = document.getElementById("side-menu");
    if (!ul) return;
    ul.querySelectorAll(".mm-active").forEach((el) => el.classList.remove("mm-active"));
    ul.querySelectorAll(".mm-show").forEach((el) => el.classList.remove("mm-show"));
    ul.querySelectorAll("a.active").forEach((el) => el.classList.remove("active"));
  }, []);

  const activateParentDropdown = useCallback((item: HTMLAnchorElement) => {
    item.classList.add("active");
    let el: HTMLElement | null = item;

    while (el && el.id !== "side-menu") {
      if (el.tagName === "LI") {
        el.classList.add("mm-active");
        const submenu = el.querySelector(":scope > ul.sub-menu");
        if (submenu) {
          submenu.classList.add("mm-show", "mm-collapse");
          submenu.classList.remove("mm-collapsing");
          (submenu as HTMLElement).style.height = "";
        }
        const link = el.querySelector(":scope > a.has-arrow, :scope > a");
        if (link) {
          link.classList.add("mm-active");
          link.setAttribute("aria-expanded", "true");
        }
      }
      el = el.parentElement;
    }

    scrollElement(item);
    return false;
  }, []);

  const location = useLocation();
  const activeMenu = useCallback(() => {
    const pathName = location.pathname;
    const fromInvoices = (location.state as { fromInvoices?: boolean } | null)?.fromInvoices;
    let matchingMenuItem: HTMLAnchorElement | null = null;
    const ul = document.getElementById("side-menu");
    if (!ul) return;

    const items = ul.getElementsByTagName("a");
    clearMenuActivation();

    for (let i = 0; i < items.length; ++i) {
      const anchor = items[i] as HTMLAnchorElement;
      // Skip toggle-only parents so route matching stays on real pages.
      const href = anchor.getAttribute("href") || "";
      if (href === "#" || href.endsWith("#")) {
        continue;
      }
      if (pathName === anchor.pathname) {
        matchingMenuItem = anchor;
        break;
      }
    }

    if (!matchingMenuItem) {
      const invoiceListLink = Array.from(items).find(
        (item) => (item as HTMLAnchorElement).pathname === "/invoices-list"
      ) as HTMLAnchorElement | undefined;

      const isInvoiceFlow =
        pathName.startsWith("/invoices-detail") ||
        (/^\/orders\/\d+/.test(pathName) && fromInvoices);

      if (invoiceListLink && isInvoiceFlow) {
        matchingMenuItem = invoiceListLink;
      }
    }

    if (matchingMenuItem) {
      activateParentDropdown(matchingMenuItem);
    }
  }, [location.pathname, location.state, activateParentDropdown, clearMenuActivation]);

  const activeMenuRef = useRef(activeMenu);
  activeMenuRef.current = activeMenu;

  useEffect(() => {
    ref.current?.recalculate?.();
  }, []);

  useEffect(() => {
    if (authLoading) return;

    const sideMenu = document.getElementById("side-menu");
    if (!sideMenu) return;

    try {
      metisRef.current?.dispose?.();
    } catch {
      // ignore
    }
    metisRef.current = null;

    // Ensure every submenu starts collapsed before MetisMenu binds.
    sideMenu.querySelectorAll("ul.sub-menu").forEach((submenu) => {
      const el = submenu as HTMLElement;
      el.classList.add("mm-collapse");
      el.classList.remove("mm-show", "mm-collapsing");
      el.style.height = "";
    });

    // Defer until after paint so permission-gated <li>s are in the DOM.
    const timer = window.setTimeout(() => {
      metisRef.current = new MetisMenu("#side-menu");
      activeMenuRef.current();
    }, 0);

    return () => {
      window.clearTimeout(timer);
      try {
        metisRef.current?.dispose?.();
      } catch {
        // ignore
      }
      metisRef.current = null;
    };
  }, [menuSignature, authLoading]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    activeMenu();
  }, [activeMenu]);

  /** Parent toggles must not use React Router paths, or navigation fights MetisMenu. */
  const preventToggleNavigation = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  function scrollElement(item: any) {
    if (item && ref.current?.getScrollElement) {
      const currentPosition = item.offsetTop;
      if (currentPosition > window.innerHeight) {
        ref.current.getScrollElement().scrollTop = currentPosition - 300;
      }
    }
  }

  return (
    <React.Fragment>
      <SimpleBar style={{ maxHeight: "100%" }} ref={ref}>
        <div id="sidebar-menu">
          <ul className="metismenu list-unstyled" id="side-menu">
            <li className="menu-title">{props.t("Menu")} </li>
            <li>
              <Link to="/dashboard" className="">
                <FeatherIcon icon="home" /> <span>{props.t("Dashboard")}</span>
              </Link>
            </li>

            {/* Orders - WooCommerce Orders */}
            <MenuPermissionGate allowCustomer anyCapability={['read_shop_order', 'read_orders']}>
              <li>
                <Link to="#" className="has-arrow" onClick={preventToggleNavigation}>
                  <FeatherIcon icon="shopping-cart" /> <span>Orders</span>
                </Link>
                <ul className="sub-menu">
                  <li>
                    <Link to="/orders-list">Orders List</Link>
                  </li>
                </ul>
              </li>
            </MenuPermissionGate>

            {/* Business Modules - Permission Protected */}
            <MenuPermissionGate allowCustomer>
              <li>
                <Link to="#" className="has-arrow" onClick={preventToggleNavigation}>
                  <FeatherIcon icon="briefcase" /> <span>Bookings</span>
                </Link>
                <ul className="sub-menu">
                  <MenuPermissionGate allowCustomer anyCapability={['read_shop_order', 'business_booking_edit_details']}>
                    <li>
                      <Link to="/bookings-list">All Bookings</Link>
                    </li>
                  </MenuPermissionGate>
                  <MenuPermissionGate anyCapability={['read_shop_order', 'business_booking_edit_details']}>
                    <li>
                      <Link to="/tours-list">Tours</Link>
                    </li>
                  </MenuPermissionGate>
                  <MenuPermissionGate anyCapability={['read_shop_order', 'business_booking_edit_details']}>
                    <li>
                      <Link to="/hotels-list">Hotels</Link>
                    </li>
                  </MenuPermissionGate>
                  <MenuPermissionGate requiredCapabilities={['edit_orders', 'business_booking_edit_details']}>
                    <li>
                      <Link to="/bookings-edit">Edit Booking</Link>
                    </li>
                  </MenuPermissionGate>
                  <MenuPermissionGate requiredCapabilities={['edit_orders', 'business_booking_assign_staff']}>
                    <li>
                      <Link to="/bookings-assign">Assign Staff</Link>
                    </li>
                  </MenuPermissionGate>
                </ul>
              </li>
            </MenuPermissionGate>

            {/* Transfers - Orders with transfer line items */}
            <MenuPermissionGate allowCustomer anyCapability={['read_shop_order', 'read_orders']}>
              <li>
                <Link to="#" className="has-arrow" onClick={preventToggleNavigation}>
                  <FeatherIcon icon="map-pin" /> <span>Transfers</span>
                </Link>
                <ul className="sub-menu">
                  <li>
                    <Link to="/transfers-list">Transfers List</Link>
                  </li>
                </ul>
              </li>
            </MenuPermissionGate>

            <MenuPermissionGate allowCustomer>
              <li>
                <Link to="/invoices-list" className="">
                  <FeatherIcon icon="file-text" /> <span>Invoices</span>
                </Link>
              </li>
            </MenuPermissionGate>

            <MenuPermissionGate allowCustomer>
              <li>
                <Link to="/apps-booking-calendar" className="">
                  <FeatherIcon icon="calendar" /> <span>{props.t("Booking Calendar")}</span>
                </Link>
              </li>
            </MenuPermissionGate>

            <MenuPermissionGate staffOnly>
              <li>
                <Link to="/reports" className="">
                  <FeatherIcon icon="bar-chart-2" /> <span>Reports</span>
                </Link>
              </li>
            </MenuPermissionGate>

            <MenuPermissionGate requiredCapabilities={['create_users', 'business_role_manage']}>
              <li>
                <Link to="#" className="has-arrow" onClick={preventToggleNavigation}>
                  <FeatherIcon icon="users" /> <span>User Management</span>
                </Link>
                <ul className="sub-menu">
                  <MenuPermissionGate requiredCapabilities={['create_users', 'business_role_manage']}>
                    <li>
                      <Link to="/users-list">User List</Link>
                    </li>
                  </MenuPermissionGate>
                  <MenuPermissionGate requiredCapabilities={['create_users', 'business_role_manage']}>
                    <li>
                      <Link to="/users-create">Create User</Link>
                    </li>
                  </MenuPermissionGate>
                  <MenuPermissionGate requiredCapabilities={['business_user_suspend']}>
                    <li>
                      <Link to="/users-suspend">Suspend Users</Link>
                    </li>
                  </MenuPermissionGate>
                </ul>
              </li>
            </MenuPermissionGate>

            <MenuPermissionGate requiredCapabilities={['manage_woocommerce', 'business_view_agent_statements']}>
              <li>
                <Link to="#" className="has-arrow" onClick={preventToggleNavigation}>
                  <FeatherIcon icon="dollar-sign" /> <span>Finance</span>
                </Link>
                <ul className="sub-menu">
                  <MenuPermissionGate requiredCapabilities={['view_woocommerce_reports', 'business_view_agent_statements']}>
                    <li>
                      <Link to="/finance-statements">Agent Statements</Link>
                    </li>
                  </MenuPermissionGate>
                  <MenuPermissionGate requiredCapabilities={['business_refund_initiate']}>
                    <li>
                      <Link to="/finance-refunds">Refunds</Link>
                    </li>
                  </MenuPermissionGate>
                  <MenuPermissionGate requiredCapabilities={['business_invoice_edit_non_structural']}>
                    <li>
                      <Link to="/finance-invoices">Invoices</Link>
                    </li>
                  </MenuPermissionGate>
                </ul>
              </li>
            </MenuPermissionGate>

            {/* Operations menu hidden from sidebar */}
            {false && (
            <MenuPermissionGate staffOnly>
              <li>
                <Link to="#" className="has-arrow" onClick={preventToggleNavigation}>
                  <FeatherIcon icon="truck" /> <span>Operations</span>
                </Link>
                <ul className="sub-menu">
                  <li>
                    <Link to="/operations-daily">Daily Operations</Link>
                  </li>
                  <MenuPermissionGate requiredCapabilities={['business_manifest_view']}>
                    <li>
                      <Link to="/operations-manifest">Manifest</Link>
                    </li>
                  </MenuPermissionGate>
                  <MenuPermissionGate requiredCapabilities={['business_logistics_manage']}>
                    <li>
                      <Link to="/operations-logistics">Logistics</Link>
                    </li>
                  </MenuPermissionGate>
                  <MenuPermissionGate requiredCapabilities={['business_assignment_manage']}>
                    <li>
                      <Link to="/operations-assignments">Assignments</Link>
                    </li>
                  </MenuPermissionGate>
                </ul>
              </li>
            </MenuPermissionGate>
            )}

            <li>
              <Link to="#" className="has-arrow" onClick={preventToggleNavigation}>
                <FeatherIcon icon="shield" /> <span>Security</span>
              </Link>
              <ul className="sub-menu">
                <li>
                  <Link to="/security-settings">Authentication &amp; 2FA</Link>
                </li>
                <li>
                  <Link to="/profile">My Profile</Link>
                </li>
              </ul>
            </li>

            {/* Apps menu hidden from sidebar */}
            {false && (
            <MenuPermissionGate staffOnly>
            <li>
              <Link to="#" className="has-arrow" onClick={preventToggleNavigation}>
                <FeatherIcon icon="grid" /> <span>{props.t("Apps")}</span>
              </Link>
              <ul className="sub-menu">
                <li>
                  <Link to="/apps-calendar">{props.t("Calendar")}</Link>
                </li>
                <li>
                  <Link to="/apps-booking-calendar">{props.t("Booking Calendar")}</Link>
                </li>
                <li>
                  <Link to="/apps-chat">{props.t("Chat")}</Link>
                </li>
                <li>
                  <Link to="#" className="has-arrow" onClick={preventToggleNavigation}>
                    <span>{props.t("Email")}</span>
                  </Link>
                  <ul className="sub-menu">
                    <li>
                      <Link to="/email-inbox">{props.t("Inbox")}</Link>
                    </li>
                    <li>
                      <Link to="/email-read">{props.t("Read Email")} </Link>
                    </li>
                  </ul>
                </li>
                <li>
                  <Link to="#" className="has-arrow" onClick={preventToggleNavigation}>
                    <span>{props.t("Invoices")}</span>
                  </Link>
                  <ul className="sub-menu">
                    <li>
                      <Link to="/invoices-list">{props.t("Invoice List")}</Link>
                    </li>
                    <li>
                      <Link to="/invoices-detail">
                        {props.t("Invoice Detail")}
                      </Link>
                    </li>
                  </ul>
                </li>
                <li>
                  <Link to="#" className="has-arrow" onClick={preventToggleNavigation}>
                    <span>{props.t("Contacts")}</span>
                  </Link>
                  <ul className="sub-menu">
                    <li>
                      <Link to="/contacts-grid">{props.t("User Grid")}</Link>
                    </li>
                    <li>
                      <Link to="/contacts-list">{props.t("User List")}</Link>
                    </li>
                    <li>
                      <Link to="/contacts-profile">{props.t("Profile")}</Link>
                    </li>
                  </ul>
                </li>
                <li>
                  <Link to="#" className="has-arrow" onClick={preventToggleNavigation}>
                    <span>{props.t("Blog")}</span>
                  </Link>
                  <ul className="sub-menu">
                    <li>
                      <Link to="/blog-grid">{props.t("Blog Grid")}</Link>
                    </li>
                    <li>
                      <Link to="/blog-list">{props.t("Blog List")}</Link>
                    </li>
                    <li>
                      <Link to="/blog-details">{props.t("Blog Details")}</Link>
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
            </MenuPermissionGate>
            )}

            {false && (
              <React.Fragment>
            <li className="menu-title">{props.t("Elements")}</li>

            <li>
              <Link to="/#" className="has-arrow ">
                <FeatherIcon icon="briefcase" /> <span>{props.t("Components")}</span>
              </Link>
              <ul className="sub-menu">
                <li>
                  <Link to="/ui-alerts">{props.t("Alerts")}</Link>
                </li>
                <li>
                  <Link to="/ui-buttons">{props.t("Buttons")}</Link>
                </li>
                <li>
                  <Link to="/ui-cards">{props.t("Cards")}</Link>
                </li>
                <li>
                  <Link to="/ui-carousel">{props.t("Carousel")}</Link>
                </li>
                <li>
                  <Link to="/ui-dropdowns">{props.t("Dropdowns")}</Link>
                </li>
                <li>
                  <Link to="/ui-grid">{props.t("Grid")}</Link>
                </li>
                <li>
                  <Link to="/ui-images">{props.t("Images")}</Link>
                </li>
                <li>
                  <Link to="/ui-modals">{props.t("Modals")}</Link>
                </li>
                <li>
                  <Link to="/ui-offcanvas">{props.t("Offcanvas")}</Link>
                </li>
                <li>
                  <Link to="/ui-progressbars">{props.t("Progress Bars")}</Link>
                </li>
                <li>
                  <Link to="/ui-placeholders">{props.t("Placeholders")}</Link>
                </li>
                <li>
                  <Link to="/ui-tabs-accordions">
                    {props.t("Tabs & Accordions")}
                  </Link>
                </li>
                <li>
                  <Link to="/ui-typography">{props.t("Typography")}</Link>
                </li>
                <li>
                  <Link to="/ui-toasts">{props.t("Toasts")}</Link>
                </li>
                <li>
                  <Link to="/ui-video">{props.t("Video")}</Link>
                </li>
                <li>
                  <Link to="/ui-general">{props.t("General")}</Link>
                </li>
                <li>
                  <Link to="/ui-colors">{props.t("Colors")}</Link>
                </li>
                <li>
                  <Link to="/ui-utilities">{props.t("Utilities")}</Link>
                </li>
              </ul>
            </li>

            <li>
              <Link to="/#" className="has-arrow ">
                <FeatherIcon icon="gift" /> <span>{props.t("Extended")}</span>
              </Link>
              <ul className="sub-menu">
                <li>
                  <Link to="/extended-lightbox">{props.t("Lightbox")}</Link>
                </li>
                <li>
                  <Link to="/extended-rangeslider">
                    {props.t("Range Slider")}
                  </Link>
                </li>
                <li>
                  <Link to="/extended-session-timeout">
                    {props.t("Session Timeout")}
                  </Link>
                </li>
                <li>
                  <Link to="/extended-rating">{props.t("Rating")}</Link>
                </li>
                <li>
                  <Link to="/extended-notifications">
                    {props.t("Notifications")}
                  </Link>
                </li>
              </ul>
            </li>

            <li>
              <Link to="/#" className="">
                <FeatherIcon icon="box" /> <span className="badge rounded-pill badge-soft-danger text-danger float-end">
                  7
                </span>
                <span>{props.t("Forms")}</span>
              </Link>
              <ul className="sub-menu">
                <li>
                  <Link to="/form-elements">{props.t("Basic Elements")}</Link>
                </li>
                <li>
                  <Link to="/form-validation">{props.t("Validation")}</Link>
                </li>
                <li>
                  <Link to="/form-advanced">{props.t("Advanced Plugins")}</Link>
                </li>
                <li>
                  <Link to="/form-uploads">{props.t("File Upload")} </Link>
                </li>
                <li>
                  <Link to="/form-wizard">{props.t("Form Wizard")}</Link>
                </li>
                <li>
                  <Link to="/form-mask">{props.t("Form Mask")}</Link>
                </li>
              </ul>
            </li>

            <li>
              <Link to="/#" className="has-arrow ">
                <FeatherIcon icon="sliders" /> <span>{props.t("Tables")}</span>
              </Link>
              <ul className="sub-menu">
                <li>
                  <Link to="/tables-basic">{props.t("Bootstrap Basic")}</Link>
                </li>
                <li>
                  <Link to="/tables-datatable">{props.t("DataTables")}</Link>
                </li>
                <li>
                  <Link to="/tables-responsive">{props.t("Responsive")}</Link>
                </li>
              </ul>
            </li>

            <li>
              <Link to="/#" className="has-arrow ">
                <FeatherIcon icon="pie-chart" /> <span>{props.t("Charts")}</span>
              </Link>

              <ul className="sub-menu">
                <li>
                  <Link to="/charts-apex">{props.t("Apexcharts")}</Link>
                </li>
                <li>
                  <Link to="/charts-echart">{props.t("Echarts")}</Link>
                </li>
                <li>
                  <Link to="/charts-chartjs">{props.t("Chartjs")}</Link>
                </li>
              </ul>
            </li>

            <li>
              <Link to="/#" className="has-arrow ">
                <FeatherIcon icon="cpu" /> <span>{props.t("Icons")}</span>
              </Link>
              <ul className="sub-menu">
                <li>
                  <Link to="/icons-boxicons">{props.t("Boxicons")}</Link>
                </li>
                <li>
                  <Link to="/icons-materialdesign">
                    {props.t("Material Design")}
                  </Link>
                </li>
                <li>
                  <Link to="/icons-dripicons">{props.t("Dripicons")}</Link>
                </li>
                <li>
                  <Link to="/icons-fontawesome">{props.t("Font awesome")}</Link>
                </li>
              </ul>
            </li>

            <li>
              <Link to="/#" className="has-arrow ">
                <FeatherIcon icon="map" /> <span>{props.t("Maps")}</span>
              </Link>
              <ul className="sub-menu">
                <li>
                  <Link to="/maps-google">{props.t("Google")}</Link>
                </li>
                <li>
                  <Link to="/maps-vector">{props.t("Vector")}</Link>
                </li>
                <li>
                  <Link to="/maps-leaflet">{props.t("Leaflet")}</Link>
                </li>
              </ul>
            </li>

            <li>
              <Link to="/#" className="has-arrow ">
                <FeatherIcon icon="share-2" /> <span>{props.t("Multi Level")}</span>
              </Link>
              <ul className="sub-menu">
                <li>
                  <Link to="/#">{props.t("Level 1.1")}</Link>
                </li>
                <li>
                  <Link to="/#" className="has-arrow">
                    {props.t("Level 1.2")}
                  </Link>
                  <ul className="sub-menu">
                    <li>
                      <Link to="/#">{props.t("Level 2.1")}</Link>
                    </li>
                    <li>
                      <Link to="/#">{props.t("Level 2.2")}</Link>
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
              </React.Fragment>
            )}
          </ul>
        </div>
      </SimpleBar>
    </React.Fragment>
  );
};

SidebarContent.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};

export default withTranslation()(withRouter(SidebarContent));
